import { jsx } from "react/jsx-runtime";
import { useComposedRefs } from "@tamagui/compose-refs";
import { isClient, isRSC, isServer, isWeb } from "@tamagui/constants";
import { stylePropsView, validPseudoKeys, validStyles } from "@tamagui/helpers";
import React, {
  Children,
  Fragment,
  createElement,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef
} from "react";
import { onConfiguredOnce } from "./config.js";
import { stackDefaultStyles } from "./constants/constants.js";
import { FontLanguageContext } from "./contexts/FontLanguageContext.js";
import { TextAncestorContext } from "./contexts/TextAncestorContext.js";
import { didGetVariableValue, setDidGetVariableValue } from "./createVariable.js";
import { extendStaticConfig, parseStaticConfig } from "./helpers/extendStaticConfig.js";
import { useSplitStyles } from "./helpers/getSplitStyles.js";
import { getAllSelectors } from "./helpers/insertStyleRule.js";
import { mergeProps } from "./helpers/mergeProps.js";
import { proxyThemeVariables } from "./helpers/proxyThemeVariables.js";
import { useShallowSetState } from "./helpers/useShallowSetState.js";
import { setMediaShouldUpdate, useMedia } from "./hooks/useMedia.js";
import { useServerRef, useServerState } from "./hooks/useServerHooks.js";
import { useThemeWithState } from "./hooks/useTheme.js";
import { hooks } from "./setupHooks.js";
import { Slot } from "./views/Slot.js";
import { useThemedChildren } from "./views/Theme.js";
process.env.TAMAGUI_TARGET;
const defaultComponentState = {
  hover: false,
  press: false,
  pressIn: false,
  focus: false,
  unmounted: true
};
const defaultComponentStateMounted = {
  ...defaultComponentState,
  unmounted: false
};
let tamaguiConfig;
let AnimatedText;
let AnimatedView;
let initialTheme;
const mouseUps = /* @__PURE__ */ new Set();
if (typeof document !== "undefined") {
  const cancelTouches = () => {
    mouseUps.forEach((x) => x());
    mouseUps.clear();
  };
  addEventListener("mouseup", cancelTouches);
  addEventListener("touchend", cancelTouches);
  addEventListener("touchcancel", cancelTouches);
}
function mergeShorthands({ defaultProps }, { shorthands }) {
  for (const key of Object.keys(defaultProps)) {
    defaultProps[shorthands[key] || key] = defaultProps[key];
  }
}
let BaseText;
let BaseView;
let hasSetupBaseViews = false;
function createComponent(configIn, ParentComponent) {
  const staticConfig = (() => {
    const next = extendStaticConfig(configIn, ParentComponent);
    if ("parsed" in next) {
      return next;
    } else {
      return parseStaticConfig(next);
    }
  })();
  const defaultComponentClassName = `is_${staticConfig.componentName}`;
  let tamaguiDefaultProps;
  let defaultTag;
  const component = forwardRef((propsIn, forwardedRef) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
    if (process.env.TAMAGUI_TARGET === "native") {
      if (!hasSetupBaseViews) {
        hasSetupBaseViews = true;
        const baseViews = (_b = (_a = hooks).getBaseViews) == null ? void 0 : _b.call(_a);
        if (baseViews) {
          BaseText = baseViews.Text;
          BaseView = baseViews.View;
        }
      }
    }
    if (process.env.NODE_ENV === "test") {
      if (propsIn["data-test-renders"]) {
        propsIn["data-test-renders"]["current"] ??= 0;
        propsIn["data-test-renders"]["current"] += 1;
      }
    }
    let props;
    if (tamaguiDefaultProps && !propsIn.asChild) {
      props = mergeProps(tamaguiDefaultProps, propsIn)[0];
    } else {
      props = propsIn;
    }
    const debugProp = props["debug"];
    const { Component, isText, isZStack } = staticConfig;
    const componentName = props.componentName || staticConfig.componentName;
    const componentClassName = props.asChild ? "" : props.componentName ? `is_${props.componentName}` : defaultComponentClassName;
    const animationsConfig = tamaguiConfig.animations;
    const useAnimations = animationsConfig == null ? void 0 : animationsConfig.useAnimations;
    const avoidClassesWhileAnimating = animationsConfig.isReactNative;
    const hasEnterStyle = !!props.enterStyle;
    const needsMount = Boolean(
      (isWeb ? isClient : true) && (hasEnterStyle || props.animation)
    );
    const states = useServerState(
      needsMount ? defaultComponentState : defaultComponentStateMounted
    );
    const state = propsIn.forceStyle ? { ...states[0], [propsIn.forceStyle]: true } : states[0];
    const setState = states[1];
    const setStateShallow = useShallowSetState(setState, debugProp, componentName);
    const stateRef = useRef(
      void 0
    );
    stateRef.current ??= {};
    const isAnimated = (() => {
      const next = !!(!staticConfig.isHOC && useAnimations && (props.animation || props.style && hasAnimatedStyleValue(props.style)));
      if (next && !stateRef.current.hasAnimated) {
        stateRef.current.hasAnimated = true;
      }
      return next || stateRef.current.hasAnimated;
    })();
    const usePresence = (_c = tamaguiConfig.animations) == null ? void 0 : _c.usePresence;
    const presence = !isRSC && isAnimated ? usePresence() : null;
    if (isAnimated && presence) {
      const presenceState = presence[2];
      if (presenceState) {
        if (state.unmounted && presenceState.enterVariant) {
          props[presenceState.enterVariant] = true;
        }
        if (!presenceState.isPresent && presenceState.exitVariant) {
          props[presenceState.exitVariant] = true;
        }
      }
    }
    const isReactNative = Boolean(
      staticConfig.isReactNative || isAnimated && tamaguiConfig.animations.isReactNative
    );
    if (process.env.NODE_ENV === "development") {
      if (debugProp) {
        const name = `${componentName || (Component == null ? void 0 : Component.displayName) || (Component == null ? void 0 : Component.name) || "[Unnamed Component]"}`;
        const type = isReactNative ? "(rnw)" : "";
        const dataIs = propsIn["data-is"] || "";
        const banner = `${name}${dataIs ? ` ${dataIs}` : ""} ${type}`;
        console.group(`%c ${banner}`, "background: yellow;");
        if (!isServer) {
          console.log("state", state);
        }
      }
    }
    const shouldAvoidClasses = !isWeb || !!(props.animation && avoidClassesWhileAnimating) || !staticConfig.acceptsClassName;
    const shouldForcePseudo = !!propsIn.forceStyle;
    const noClassNames = shouldAvoidClasses || shouldForcePseudo;
    const themeState = useThemeWithState({
      name: props.theme,
      componentName,
      reset: props.reset,
      inverse: props.themeInverse,
      // @ts-expect-error
      disable: props["data-themeable"],
      debug: props.debug,
      shouldUpdate: () => !!stateRef.current.didAccessThemeVariableValue
    });
    const hasTextAncestor = !!(isWeb && isText ? useContext(TextAncestorContext) : false);
    const languageContext = isRSC ? null : useContext(FontLanguageContext);
    const isDisabled = props.disabled ?? ((_d = props.accessibilityState) == null ? void 0 : _d.disabled);
    const isTaggable = !Component || typeof Component === "string";
    const element = isWeb ? isTaggable ? props.tag || defaultTag || Component : Component : Component;
    const BaseTextComponent = BaseText || element || "span";
    const BaseViewComponent = BaseView || element || (hasTextAncestor ? "span" : "div");
    let elementType = isText ? (isAnimated ? AnimatedText : null) || BaseTextComponent : (isAnimated ? AnimatedView : null) || BaseViewComponent;
    elementType = Component || elementType;
    const isStringElement = typeof elementType === "string";
    const isExiting = (presence == null ? void 0 : presence[0]) === false;
    const mediaState = useMedia(
      // @ts-ignore, we just pass a stable object so we can get it later with
      // should match to the one used in `setMediaShouldUpdate` below
      stateRef,
      debugProp ? { props, staticConfig } : null
    );
    setDidGetVariableValue(false);
    const splitStyles = useSplitStyles(
      props,
      staticConfig,
      themeState.theme,
      {
        ...state,
        mediaState,
        noClassNames,
        dynamicStylesInline: noClassNames,
        hasTextAncestor,
        resolveVariablesAs: "auto",
        isExiting
      },
      null,
      languageContext || void 0,
      elementType,
      debugProp
    );
    const isMediaSpaced = Array.isArray(splitStyles.hasMedia);
    const shouldListenForMedia = didGetVariableValue() || isMediaSpaced || noClassNames && splitStyles.hasMedia === true;
    setMediaShouldUpdate(stateRef, {
      enabled: shouldListenForMedia,
      keys: noClassNames && isMediaSpaced ? splitStyles.hasMedia : null
    });
    const hostRef = useServerRef(null);
    const isAnimatedReactNativeWeb = isAnimated && avoidClassesWhileAnimating;
    if (process.env.NODE_ENV === "development") {
      if (!process.env.TAMAGUI_TARGET) {
        console.error(
          `No process.env.TAMAGUI_TARGET set, please set it to "native" or "web".`
        );
      }
      if (debugProp) {
        console.groupCollapsed("props");
        console.log("props in", propsIn, "mapped to", props, "in order", Object.keys(props));
        console.log("splitStyles", splitStyles);
        console.log("shouldListenForMedia", shouldListenForMedia);
        console.log("className", Object.values(splitStyles.classNames));
        if (isClient) {
          console.log("ref", hostRef, "(click to view)");
        }
        console.groupEnd();
        if (debugProp === "break") {
          debugger;
        }
      }
    }
    const {
      viewProps: viewPropsIn,
      pseudos,
      style: splitStylesStyle,
      classNames,
      space
    } = splitStyles;
    const propsWithAnimation = props;
    let animationStyles;
    if (!isRSC && isAnimated && useAnimations && !staticConfig.isHOC) {
      const animations = useAnimations({
        props: propsWithAnimation,
        style: splitStylesStyle,
        presence,
        state,
        pseudos: pseudos || null,
        onDidAnimate: props.onDidAnimate,
        hostRef,
        staticConfig
      });
      if (animations) {
        animationStyles = animations.style;
      }
    }
    const {
      asChild,
      children,
      onPress,
      onPressIn,
      onPressOut,
      onHoverIn,
      onHoverOut,
      themeShallow,
      spaceDirection: _spaceDirection,
      disabled: disabledProp,
      onMouseUp,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      separator,
      // ignore from here on out
      forceStyle: _forceStyle,
      // @ts-ignore  for next/link compat etc
      onClick,
      theme: _themeProp,
      // @ts-ignore
      defaultVariants,
      ...nonTamaguiProps
    } = viewPropsIn;
    const disabled = ((_e = props.accessibilityState) == null ? void 0 : _e.disabled) || props.accessibilityDisabled;
    let viewProps = nonTamaguiProps;
    if (process.env.TAMAGUI_TARGET === "web" && !isReactNative && !asChild) {
      viewProps = (_g = (_f = hooks).usePropsTransform) == null ? void 0 : _g.call(_f, elementType, nonTamaguiProps, hostRef);
    } else {
      viewProps = nonTamaguiProps;
    }
    viewProps.ref = useComposedRefs(hostRef, forwardedRef);
    if (process.env.NODE_ENV === "development") {
      if (!isText && isWeb && !staticConfig.isHOC) {
        Children.toArray(props.children).forEach((item) => {
          if (typeof item === "string" && item !== "\n") {
            console.error(
              `Unexpected text node: ${item}. A text node cannot be a child of a <View>.`
            );
          }
        });
      }
    }
    const unPress = useCallback(() => {
      setStateShallow({
        press: false,
        pressIn: false
      });
    }, [setStateShallow]);
    if (isWeb) {
      useEffect(() => {
        return () => {
          mouseUps.delete(unPress);
        };
      }, []);
    }
    const shouldSetMounted = needsMount && state.unmounted;
    useEffect(() => {
      if (!shouldSetMounted)
        return;
      if (state.unmounted === true && needsMount) {
        setStateShallow({
          unmounted: false
        });
        return;
      }
      setStateShallow({
        unmounted: false
      });
    }, [shouldSetMounted, state.unmounted]);
    let styles;
    if (isStringElement && shouldAvoidClasses && !shouldForcePseudo) {
      styles = {
        ...animationStyles ?? splitStylesStyle
      };
    } else {
      styles = [animationStyles ?? splitStylesStyle];
      if (shouldForcePseudo) {
        const next = {};
        styles.forEach((style) => Object.assign(next, style));
        Object.assign(splitStyles.style, next);
      }
    }
    let fontFamily = isText ? splitStyles.fontFamily || staticConfig.defaultProps.fontFamily : null;
    if (fontFamily && fontFamily[0] === "$") {
      fontFamily = fontFamily.slice(1);
    }
    const fontFamilyClassName = fontFamily ? `font_${fontFamily}` : "";
    const classList = [
      hasEnterStyle && (state.unmounted && needsMount || !isClient) ? "t_will-mount" : "",
      componentName ? componentClassName : "",
      fontFamilyClassName,
      classNames ? Object.values(classNames).join(" ") : ""
    ];
    const className = classList.join(" ");
    if (process.env.TAMAGUI_TARGET === "web") {
      const style = animationStyles ?? splitStyles.style;
      if (isAnimatedReactNativeWeb) {
        viewProps.style = style;
      } else if (isReactNative) {
        const cnStyles = { $$css: true };
        for (const name of className.split(" ")) {
          cnStyles[name] = name;
        }
        viewProps.style = [...Array.isArray(style) ? style : [style], cnStyles];
        if (process.env.NODE_ENV === "development") {
          Object.keys(viewProps).forEach((key) => {
            if (key.startsWith("data-")) {
              viewProps.dataSet ??= {};
              viewProps.dataSet[key.replace("data-", "")] = viewProps[key];
              delete viewProps[key];
            }
          });
        }
      } else {
        viewProps.className = className;
        viewProps.style = style;
      }
    }
    if (process.env.TAMAGUI_TARGET === "native") {
      if (splitStyles.fontFamily) {
        const faceInfo = (_h = tamaguiConfig.fontsParsed[splitStyles.fontFamily]) == null ? void 0 : _h.face;
        if (faceInfo) {
          const [weight, style] = (() => {
            var _a2, _b2;
            let weight2;
            let style2;
            for (let i = styles.length; i >= 0; i--) {
              weight2 ??= (_a2 = styles[i]) == null ? void 0 : _a2.fontWeight;
              style2 ??= (_b2 = styles[i]) == null ? void 0 : _b2.fontStyle;
            }
            return [weight2 || "400", style2 || "normal"];
          })();
          const overrideFace = (_j = (_i = faceInfo[weight]) == null ? void 0 : _i[style]) == null ? void 0 : _j.val;
          if (overrideFace) {
            for (const style2 of styles) {
              if (style2 == null ? void 0 : style2.fontFamily) {
                style2.fontFamily = overrideFace;
                style2.fontWeight = void 0;
                style2.fontStyle = void 0;
              }
            }
          }
        }
      }
      viewProps.style = styles;
    }
    const runtimePressStyle = !disabled && noClassNames && (pseudos == null ? void 0 : pseudos.pressStyle);
    const attachPress = Boolean(
      runtimePressStyle || onPress || onPressOut || onPressIn || onClick
    );
    const runtimeHoverStyle = !disabled && noClassNames && (pseudos == null ? void 0 : pseudos.hoverStyle);
    const isHoverable = isWeb && !!(runtimeHoverStyle || onHoverIn || onHoverOut || onMouseEnter || onMouseLeave);
    const handlesPressEvents = !(isWeb || asChild);
    const shouldAttach = Boolean(
      attachPress || isHoverable || noClassNames && "pressStyle" in props || isWeb && noClassNames && "hoverStyle" in props
    );
    const events = shouldAttach && !isRSC && !isDisabled && !asChild ? {
      onPressOut: attachPress ? (e) => {
        unPress();
        onPressOut == null ? void 0 : onPressOut(e);
        onMouseUp == null ? void 0 : onMouseUp(e);
      } : void 0,
      ...isHoverable && {
        onMouseEnter: isHoverable ? (e) => {
          const next = {};
          if (isHoverable) {
            next.hover = true;
          }
          if (state.pressIn) {
            next.press = true;
          }
          if (isHoverable || state.pressIn) {
            setStateShallow(next);
          }
          onHoverIn == null ? void 0 : onHoverIn(e);
          onMouseEnter == null ? void 0 : onMouseEnter(e);
        } : void 0,
        onMouseLeave: isHoverable ? (e) => {
          const next = {};
          mouseUps.add(unPress);
          if (isHoverable) {
            next.hover = false;
          }
          if (state.pressIn) {
            next.press = false;
            next.pressIn = false;
          }
          if (Object.keys(next).length) {
            setStateShallow(next);
          }
          onHoverOut == null ? void 0 : onHoverOut(e);
          onMouseLeave == null ? void 0 : onMouseLeave(e);
        } : void 0
      },
      onPressIn: attachPress ? (e) => {
        setStateShallow({
          press: true,
          pressIn: true,
          hover: false
        });
        onPressIn == null ? void 0 : onPressIn(e);
        onMouseDown == null ? void 0 : onMouseDown(e);
        if (isWeb) {
          mouseUps.add(unPress);
        }
      } : void 0,
      onPress: attachPress ? (e) => {
        unPress();
        isWeb && (onClick == null ? void 0 : onClick(e));
        onPress == null ? void 0 : onPress(e);
      } : void 0
    } : null;
    if (process.env.TAMAGUI_TARGET === "native") {
      if (events) {
        Object.assign(events, {
          cancelable: !props.rejectResponderTermination,
          disabled: isDisabled,
          hitSlop: props.hitSlop,
          delayLongPress: props.delayLongPress,
          delayPressIn: props.delayPressIn,
          delayPressOut: props.delayPressOut,
          focusable: viewProps.focusable ?? true,
          minPressDuration: 0
        });
      }
    }
    (_l = (_k = hooks).useEvents) == null ? void 0 : _l.call(_k, viewProps, events, splitStyles, setStateShallow);
    const shouldReset = !!(themeShallow && themeState.isNewTheme);
    if (shouldReset) {
      stateRef.current.themeShallow = true;
    }
    let content = !children || asChild ? children : spacedChildren({
      separator,
      children,
      space,
      direction: props.spaceDirection || "both",
      isZStack,
      debug: debugProp
    });
    if (asChild) {
      elementType = Slot;
      viewProps = {
        ...viewProps,
        onPress,
        onPressIn,
        onPressOut
      };
    }
    content = createElement(elementType, viewProps, content);
    content = useThemedChildren(themeState, content, {
      shallow: stateRef.current.themeShallow
      // passPropsToChildren: true,
    });
    if (process.env.TAMAGUI_TARGET === "web") {
      if (events || isAnimatedReactNativeWeb) {
        content = /* @__PURE__ */ jsx(
          "span",
          {
            className: `${isAnimatedReactNativeWeb ? className : ""}  _dsp_contents`,
            ...events && {
              onMouseEnter: events.onMouseEnter,
              onMouseLeave: events.onMouseLeave,
              onClick: events.onPress,
              onMouseDown: events.onPressIn,
              onMouseUp: events.onPressOut,
              onTouchStart: events.onPressIn,
              onTouchEnd: events.onPressOut
            },
            children: content
          }
        );
      }
    }
    if (process.env.NODE_ENV === "development" && process.env.DEBUG !== "tamagui") {
      if (debugProp) {
        const element2 = typeof elementType === "string" ? elementType : "Component";
        console.groupCollapsed(`render <${element2} /> with props`, viewProps);
        for (const key in viewProps) {
          console.log(key, viewProps[key]);
        }
        console.log("children", content);
        console.groupEnd();
        if (typeof window !== "undefined") {
          console.log({ state, themeState, isAnimated, isAnimatedReactNativeWeb, tamaguiDefaultProps, viewProps, splitStyles, animationStyles, handlesPressEvents, isStringElement, classNamesIn: (_m = props.className) == null ? void 0 : _m.split(" "), classNamesOut: (_n = viewProps.className) == null ? void 0 : _n.split(" "), events, shouldAttach, styles, pseudos, content, shouldAvoidClasses, avoidClasses: avoidClassesWhileAnimating, animation: props.animation, style: splitStylesStyle, staticConfig, tamaguiConfig, shouldForcePseudo, classNamesFull: Object.fromEntries(Object.entries(classNames).map(([k, v]) => [v, getAllSelectors()[v]])) });
        }
        console.groupEnd();
      }
    }
    return content;
  });
  if (staticConfig.componentName) {
    component.displayName = staticConfig.componentName;
  }
  onConfiguredOnce((conf) => {
    if (!tamaguiConfig) {
      tamaguiConfig = conf;
      if (tamaguiConfig.animations) {
        AnimatedText = tamaguiConfig.animations.Text;
        AnimatedView = tamaguiConfig.animations.View;
      }
      if (!initialTheme) {
        const next = conf.themes[Object.keys(conf.themes)[0]];
        initialTheme = proxyThemeVariables(next);
        if (process.env.NODE_ENV === "development") {
          if (!initialTheme) {
            console.log("Warning: Missing theme");
          }
        }
      }
    }
    mergeShorthands(staticConfig, tamaguiConfig);
    let defaultPropsIn = staticConfig.defaultProps || {};
    const parentNames = [...staticConfig.parentNames || [], staticConfig.componentName];
    if (tamaguiConfig.defaultProps && parentNames && staticConfig.componentName) {
      defaultPropsIn = mergeConfigDefaultProps(
        staticConfig.componentName,
        defaultPropsIn,
        tamaguiConfig.defaultProps,
        parentNames,
        tamaguiConfig
      );
    }
    const debug = defaultPropsIn["debug"];
    const [ourProps, ourClassNames] = mergeProps(defaultPropsIn, {});
    if (ourProps.tag) {
      defaultTag = ourProps.tag;
    }
    const noClassNames = !staticConfig.acceptsClassName;
    const { name, variants, defaultVariants, ...restProps } = ourProps;
    const [defaults, defaultsClassnames] = mergeProps(component.defaultProps, {
      ...defaultVariants,
      ...restProps
    });
    if (!isWeb) {
      for (const key in staticConfig.defaultProps) {
        const val = staticConfig.defaultProps[key];
        if (validPseudoKeys[key])
          continue;
        defaults[key] = val;
      }
    }
    tamaguiDefaultProps = defaults;
    if (process.env.NODE_ENV === "development" && debug) {
      if (process.env.IS_STATIC !== "is_static") {
        console.log(`\u{1F41B} [${staticConfig.componentName || "Component"}]`, {
          staticConfig,
          tamaguiDefaultProps,
          defaults,
          defaultPropsIn,
          defaultPropsKeyOrder: Object.keys(staticConfig.defaultProps),
          defaultPropsInKeyOrder: Object.keys(defaultPropsIn).map((k) => [
            k,
            defaultPropsIn[k]
          ]),
          ourProps,
          ourClassNames,
          defaultsClassnames,
          defaultTag,
          noClassNames
        });
      }
    }
  });
  let res = component;
  if (configIn.memo) {
    res = memo(res);
  }
  res.staticConfig = {
    validStyles: staticConfig.validStyles || stylePropsView,
    ...staticConfig
  };
  res.extractable = (Component, conf) => {
    Component.staticConfig = extendStaticConfig(
      {
        Component,
        ...conf,
        neverFlatten: true,
        isHOC: true,
        defaultProps: {
          ...Component.defaultProps,
          ...conf == null ? void 0 : conf.defaultProps
        }
      },
      res
    );
    return Component;
  };
  return res;
}
const Unspaced = (props) => {
  return props.children;
};
Unspaced["isUnspaced"] = true;
const Spacer = createComponent({
  acceptsClassName: true,
  memo: true,
  componentName: "Spacer",
  validStyles,
  defaultProps: {
    ...stackDefaultStyles,
    // avoid nesting issues
    tag: "span",
    size: true,
    pointerEvents: "none"
  },
  variants: {
    size: {
      "...size": (size, { tokens }) => {
        size = size === true ? "$true" : size;
        const sizePx = tokens.space[size] ?? size;
        return {
          width: sizePx,
          height: sizePx,
          minWidth: sizePx,
          minHeight: sizePx
        };
      }
    },
    flex: {
      true: {
        flexGrow: 1
      }
    },
    direction: {
      horizontal: {
        height: 0,
        minHeight: 0
      },
      vertical: {
        width: 0,
        minWidth: 0
      },
      both: {}
    }
  }
});
function spacedChildren(props) {
  var _a, _b, _c;
  const { isZStack, children, space, direction, spaceFlex, separator } = props;
  const hasSpace = !!(space || spaceFlex);
  const hasSeparator = !(separator === void 0 || separator === null);
  if (!(hasSpace || hasSeparator || isZStack)) {
    return children;
  }
  const childrenList = Children.toArray(children);
  const len = childrenList.length;
  if (len <= 1 && !isZStack && !((_b = (_a = childrenList[0]) == null ? void 0 : _a["type"]) == null ? void 0 : _b["shouldForwardSpace"])) {
    return childrenList;
  }
  const final = [];
  for (let [index, child] of childrenList.entries()) {
    const isEmpty = child === null || child === void 0 || Array.isArray(child) && child.length === 0;
    if (!isEmpty && React.isValidElement(child) && ((_c = child.type) == null ? void 0 : _c["shouldForwardSpace"])) {
      child = React.cloneElement(child, {
        space,
        spaceFlex,
        separator,
        key: child.key
      });
    }
    if (isEmpty || !child || child["key"] && !isZStack) {
      final.push(child);
    } else {
      final.push(
        /* @__PURE__ */ jsx(Fragment, { children: isZStack ? /* @__PURE__ */ jsx(AbsoluteFill, { children: child }) : child }, index)
      );
    }
    if (isUnspaced(child) && index === 0)
      continue;
    if (isZStack)
      continue;
    const next = childrenList[index + 1];
    if (next && !isUnspaced(next)) {
      if (separator) {
        if (hasSpace) {
          final.push(
            createSpacer({
              key: `_${index}_00tmgui`,
              direction,
              space,
              spaceFlex
            })
          );
        }
        final.push(
          React.isValidElement(separator) ? React.cloneElement(separator, { key: `sep_${index}` }) : separator
        );
        if (hasSpace) {
          final.push(
            createSpacer({
              key: `_${index}01tmgui`,
              direction,
              space,
              spaceFlex
            })
          );
        }
      } else {
        final.push(
          createSpacer({
            key: `_${index}02tmgui`,
            direction,
            space,
            spaceFlex
          })
        );
      }
    }
  }
  if (process.env.NODE_ENV === "development") {
    if (props.debug) {
      console.log(`  Spaced children`, final, props);
    }
  }
  return final;
}
function createSpacer({ key, direction, space, spaceFlex }) {
  return /* @__PURE__ */ jsx(
    Spacer,
    {
      size: space,
      direction,
      ...typeof spaceFlex !== "undefined" && {
        flex: spaceFlex === true ? 1 : spaceFlex === false ? 0 : spaceFlex
      }
    },
    key
  );
}
function isUnspaced(child) {
  const t = child == null ? void 0 : child["type"];
  return (t == null ? void 0 : t["isVisuallyHidden"]) || (t == null ? void 0 : t["isUnspaced"]);
}
const DefaultProps = /* @__PURE__ */ new Map();
function mergeConfigDefaultProps(name, props, configDefaults, parentNames, conf) {
  const len = parentNames.length;
  let prev;
  for (let i = 0; i < len; i++) {
    const n = parentNames[i];
    if (!n)
      continue;
    if (DefaultProps.has(n)) {
      prev = DefaultProps.get(n);
      continue;
    }
    const props2 = configDefaults[n];
    if (!props2) {
      if (prev) {
        DefaultProps.set(n, prev);
      }
      continue;
    }
    prev = mergeProps(prev || {}, props2, false, conf.inverseShorthands)[0];
    DefaultProps.set(n, prev);
  }
  const ourDefaultsMerged = DefaultProps.get(name);
  if (ourDefaultsMerged) {
    return mergeProps(props, ourDefaultsMerged, false, conf.inverseShorthands)[0];
  }
  return props;
}
const AbsoluteFill = createComponent({
  defaultProps: {
    ...stackDefaultStyles,
    flexDirection: "column",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: "box-none"
  }
});
function hasAnimatedStyleValue(style) {
  return Object.keys(style).some((k) => {
    const val = style[k];
    return val && typeof val === "object" && "_animation" in val;
  });
}
export {
  Spacer,
  Unspaced,
  createComponent,
  defaultComponentState,
  mouseUps,
  spacedChildren
};
//# sourceMappingURL=createComponent.js.map
